import { useMutation } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import { forwardRef, useRef, useState } from "react";
import {
  AskAiChatMessageFragmentFragment,
  ChatSessionMessageReactionFragmentFragment,
  ChatSessionReactionType,
  ChatbotRoleEnum,
  CreateChatMessageReactionMutation,
  CreateChatMessageReactionMutationVariables,
} from "types/graphql-schema";

import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { sanitizeMarkdown } from "@helpers/string";

import createChatMessageReactionMutation from "../graphql/create-chat-message-reaction-mutation";
import { parsePartialJson } from "../helpers";
import AskAiEmbeds from "./ask-ai-embeds";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import AskAiPlaceholders from "./ask-ai-placeholders";
import AskAIToolCalls from "./ask-ai-tool-calls";

export const AskAIMessage = forwardRef(
  (
    {
      message,
      chatSessionId,
      reaction,
      isLastMessage,
      className,
      style,
    }: {
      message: AskAiChatMessageFragmentFragment;
      chatSessionId: number | null;
      reaction: ChatSessionMessageReactionFragmentFragment | null;
      isLastMessage: boolean;
      className?: string;
      style?: React.CSSProperties;
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const { isStreaming, content } = message;
    const isStreamingJSON = isStreaming && content.trim().startsWith("{");
    const localStreamedContent = isStreamingJSON
      ? parsePartialJson(content)
      : null;

    const [showCommentBox, setShowCommentBox] = useState(false);
    const [comment, setComment] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [createChatReaction, { loading: isSubmittingReaction }] = useMutation<
      CreateChatMessageReactionMutation,
      CreateChatMessageReactionMutationVariables
    >(createChatMessageReactionMutation);

    const handleClickThumbsUp = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsUp,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
        },
      });
    };
    const handleClickThumbsDown = () => {
      if (!chatSessionId) {
        return;
      }
      setShowCommentBox(true);
    };

    const handleSubmitDislike = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsDown,
          comment: comment || undefined,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
          setShowCommentBox(false);
          setComment("");
        },
      });
    };

    const isThumbsUp =
      !!reaction && reaction.reactionType === ChatSessionReactionType.ThumbsUp;
    const isThumbsDown =
      !!reaction &&
      reaction.reactionType === ChatSessionReactionType.ThumbsDown;

    let contentJson = null;
    try {
      contentJson = JSON.parse(content);
    } catch {
      // Intentionally empty - invalid JSON is handled by contentJson being null
    }

    const localStreamedText = localStreamedContent?.text ?? "";
    const markdownContent =
      (isStreamingJSON
        ? // sanitize the markdown to avoid unclosed opening brackets
          sanitizeMarkdown(localStreamedText)
        : isStreaming
          ? sanitizeMarkdown(content)
          : content) || "";

    const toolCalls = compact(message.toolCalls || []);

    return (
      <ErrorBoundary fallback={ErrorFallback}>
        <div
          className={classNames(
            !isLastMessage && "group/message-reaction",
            className,
          )}
          style={style}
          ref={ref}
        >
          {message.role === ChatbotRoleEnum.Assistant &&
            toolCalls.length > 0 && (
              <div className="mb-3">
                <AskAIToolCalls toolCalls={toolCalls} />
              </div>
            )}
          <div
            className={classNames(
              "w-full text-gray-700 flex",
              message.role === ChatbotRoleEnum.User
                ? "justify-end pl-12"
                : "flex-col gap-4",
            )}
            key={message.id}
            id={message.id}
          >
            {!contentJson && !!markdownContent && (
              <div
                className={classNames(
                  "prose prose-sm max-w-none",
                  "bg-white",
                  message.role === ChatbotRoleEnum.User &&
                    "rounded-3xl py-2 px-4 bg-gray-100",
                  message.error && "text-red-600",
                )}
              >
                <AskAIMessageMarkdown>{markdownContent}</AskAIMessageMarkdown>
              </div>
            )}
            <div className="empty:hidden">
              {/* {isLastMessage && ( // testing
                <div className="outline outline-red-500/20">
                  <AskAiPlaceholders
                    content={{
                      output_items: [
                        {
                          type: "plain_text",
                          text: "Here are the goals I found for you",
                        },
                        {
                          type: "goal_list",
                          items: [{ type: "goal", id: 7 }],
                        },
                      ],
                    }}
                  />
                </div>
              )} */}
              {/* while it is streaming, we are trying to display as much informations as we can and as fast. We call those placeholders. */}
              {isStreamingJSON ? (
                <AskAiPlaceholders
                  content={localStreamedContent}
                  messageId={message.id || "tmp"}
                />
              ) : (
                // once streaming is complete, we render the final embeds
                <AskAiEmbeds
                  chatSessionId={chatSessionId}
                  message={message}
                  isLastMessage={isLastMessage}
                />
              )}
            </div>
          </div>
          {message.role === ChatbotRoleEnum.Assistant && (
            <div
              className={classNames(
                "flex gap-1 items-center mt-2",
                !isLastMessage &&
                  !reaction &&
                  "opacity-0 group-hover/message-reaction:opacity-100",
              )}
            >
              <Button
                icon={getTopicflowIcon("thumbsUp")}
                onClick={handleClickThumbsUp}
                theme={buttonTheme.iconGray}
                disabled={isThumbsUp || !!isStreaming}
                className={classNames(
                  isThumbsUp && "opacity-100 text-gray-900",
                  isThumbsDown && "opacity-50 hover:opacity-100",
                )}
                aria-label="Thumbs up"
              />
              <Button
                icon={getTopicflowIcon("thumbsDown")}
                onClick={handleClickThumbsDown}
                theme={buttonTheme.iconGray}
                disabled={isThumbsDown || !!isStreaming}
                className={classNames(
                  isThumbsDown && "opacity-100 text-gray-900",
                  isThumbsUp && "opacity-50 hover:opacity-100",
                )}
                aria-label="Thumbs down"
              />
            </div>
          )}
          {message.role === ChatbotRoleEnum.Assistant && showCommentBox && (
            <Modal
              open={showCommentBox}
              onClose={() => {
                setShowCommentBox(false);
                setComment("");
              }}
              alignedTop
              initialFocus={textareaRef}
            >
              <div className="p-6 flex flex-col gap-4">
                <ModalTitle
                  onClose={() => {
                    setShowCommentBox(false);
                    setComment("");
                  }}
                  className="text-sm"
                >
                  What could be improved?
                </ModalTitle>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Tell us what went wrong (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  ref={textareaRef}
                  aria-label="Feedback comment"
                />
                <div className="flex gap-2 justify-end items-center">
                  {isSubmittingReaction && <Loading mini size="5" />}
                  <Button
                    onClick={() => {
                      setShowCommentBox(false);
                      setComment("");
                    }}
                    theme={buttonTheme.default}
                    className="text-sm"
                    disabled={isSubmittingReaction}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitDislike}
                    theme={buttonTheme.primary}
                    className="text-sm"
                    disabled={isSubmittingReaction}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </ErrorBoundary>
    );
  },
);

export default AskAIMessage;
